/**
 * buildLaunchPayload — assemble the per-action LIST payload for the reshaped
 * mortgagee launch contract, and assemble it ONCE.
 *
 * Every submit seam goes through here: the `LaunchForm` test harness, and (from
 * Phase 5) the drawer's `handleLaunch` and `handleRetry`. One assembler means the
 * effective-date fallback, the reply-draft key and the declared-key filter cannot
 * disagree between the path the tests exercise and the path the AM presses.
 *
 * The single most important invariant in this story lives here: **the submitted
 * payload contains no key the declared schema does not declare** (AC-6c). The
 * substrate's `model_validate(extra="ignore")` silently discards anything else,
 * and three defects have already been traced to exactly that. Filtering
 * client-side turns a silent discard into a reported one — `dropped` names every
 * removed key so the caller can log it.
 *
 * Pure: no React, no Office.js, no network.
 */

import {
  ADDRESS_PART_KEYS,
  composeMortgageeValues,
} from "~/common/workflows/launch/addressParts";
import {
  findPolicyOption,
  POLICY_TERM_KEY_FIELD,
  readPolicyOptions,
  readPolicyTermKey,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  mergeLaunchSlice,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";

/**
 * Working keys the grouped form carries that are NOT, and never were, declared
 * workflow inputs.
 *
 * A REPORTING exemption, never an allow-list widening: these are removed from
 * the payload exactly like any other undeclared key, they are simply not added
 * to `dropped`. The exemption exists so the caller's dropped-key warning names
 * real defects only — a "dropped `policy_term_key`" / "dropped
 * `interest_options`" line on EVERY launch is precisely how a real dropped-key
 * warning gets ignored.
 *
 *  - `policy_term_key` — the freeform term identity that rides `values`; its own
 *    docstring records that the server strips it with `extra='ignore'`.
 *  - `policy_options` / `policy_number_resolution` — the server's prefill echoes.
 *  - `interest_options` / `interest_options_by_line` / `interest_options_line_sk`
 *    — the interest-target prefill echoes `readInterestOptionsForSelection`
 *    reads, same freeform contract.
 *  - the four `ADDRESS_PART_KEYS` — consumed by `composeMortgageeValues` into
 *    the declared `mailing_address` before the filter runs.
 */
export const FORM_STATE_ONLY_KEYS: ReadonlySet<string> = new Set<string>([
  POLICY_TERM_KEY_FIELD,
  "policy_options",
  "policy_number_resolution",
  "interest_options",
  "interest_options_by_line",
  "interest_options_line_sk",
  ...ADDRESS_PART_KEYS,
]);

/**
 * The reply-draft value as BOTH the rendered switch and the payload read it.
 *
 * Exported so `ReplyDraftControl`'s checked state and the key this builder
 * writes cannot drift: a switch showing ON beside a payload carrying `false`
 * (or the reverse) is the AC-5 defect with the control's own affordance
 * vouching for it. A non-boolean value — nothing renders one, but the values
 * record is freeform — reads as the untouched default rather than coercing
 * `"false"` to `true` through JavaScript truthiness.
 */
export function replyDraftValue(raw: unknown): boolean {
  return typeof raw === "boolean" ? raw : true;
}

export interface BuildLaunchPayloadArgs {
  binding: LaunchBinding;
  /** Run-level half of the form's values (`splitLaunchValues`). */
  runLevelValues: Record<string, unknown>;
  /** The action objects (`splitLaunchValues`). */
  actions: Record<string, unknown>[];
  /**
   * Run-level values only the LAUNCHER knows: `idempotency_key`,
   * `graph_message_id` / `internet_message_id`, `client_sk` / `client_name`,
   * `oversight_level`, `use_sp_login`. Merged OVER the form's run level and
   * filtered by the SAME allow-list — an injected key the schema does not
   * declare is dropped and REPORTED, never smuggled through.
   *
   * The effective-date fallback is deliberately NOT here: it belongs inside the
   * action (see below).
   */
  injected?: Record<string, unknown>;
}

export interface BuiltLaunchPayload {
  /** The `inputs` object to hand `startWorkflow`. */
  inputs: Record<string, unknown>;
  /** Every key the declared-key filter removed, qualified by where it sat
   *  (`old_key`, `actions[0].old_key`, `actions[0].interests[1].old_key`), for
   *  the caller to log. Excludes `FORM_STATE_ONLY_KEYS`. */
  dropped: string[];
}

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

/** Keep only `allowed` keys; push every other key onto `dropped` under `path`. */
function filterDeclared(
  record: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  path: string,
  dropped: string[],
): Record<string, unknown> {
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (allowed.has(key)) {
      kept[key] = value;
      continue;
    }
    if (!FORM_STATE_ONLY_KEYS.has(key)) dropped.push(`${path}${key}`);
  }
  return kept;
}

/**
 * Assemble the outbound launch payload.
 *
 * Throws — loudly, before any network call — when asked to build a payload the
 * contract cannot express: a non-reshaped binding (the flat path has its own
 * assembler and must not come through here) or more actions than the declared
 * gate allows (AC-7).
 */
export function buildLaunchPayload({
  binding,
  runLevelValues,
  actions,
  injected,
}: BuildLaunchPayloadArgs): BuiltLaunchPayload {
  const actionsKey = binding.actionsKey;
  if (!binding.isReshaped || !actionsKey || !binding.actionItemSchema) {
    throw new Error(
      "buildLaunchPayload: the declared schema has no action list. The flat " +
        "launch path must not be assembled here.",
    );
  }
  if (actions.length > binding.maxActions) {
    throw new Error(
      `buildLaunchPayload: ${actions.length} actions requested but the ` +
        `declared gate allows ${binding.maxActions}.`,
    );
  }

  const dropped: string[] = [];
  const recordsKey = binding.roles.interestRecords;
  const effectiveDateKey = binding.roles.effectiveDate;

  const builtActions = actions.map((action, index) => {
    // The merged READ view — the only record the lifted helpers understand,
    // because the term identity lives on the action while `policy_options` and
    // `policy_term_key` stay at run level. Never spread back into the action.
    const flat = mergeLaunchSlice(binding, runLevelValues, action);
    const chosenTerm = findPolicyOption(
      readPolicyOptions(flat),
      typeof flat.policy_number === "string" ? flat.policy_number : null,
      readPolicyTermKey(flat),
    );

    // Compose the address BEFORE filtering, so the four part keys never reach
    // the wire and `mailing_address` exists to be kept. Called on the ACTION with
    // the DECLARED records key: the unparameterised call would look for a
    // `mortgagees` key the reshaped action may not have, return the action
    // unchanged, and let the parts be stripped with `mailing_address` never
    // composed and nothing reported — a silent address loss.
    const composed = recordsKey
      ? composeMortgageeValues(action, recordsKey)
      : { ...action };

    // Blank effective date → the SELECTED TERM's effective date, applied onto
    // the ACTION. Putting this in run-level `injected` (where today's
    // `handleLaunch` puts it) would filter it straight back out of a reshaped
    // payload, a blank date would ship, and `_normalize_effective_date` would
    // default to TODAY — which for a future renewal term falls outside the
    // policy period entirely. That is the 2026-08-05 defect this fallback closes.
    const withFallback: Record<string, unknown> = { ...composed };
    if (
      effectiveDateKey &&
      isBlank(withFallback[effectiveDateKey]) &&
      !isBlank(chosenTerm?.effective_date)
    ) {
      withFallback[effectiveDateKey] = chosenTerm?.effective_date;
    }

    const kept = filterDeclared(
      withFallback,
      binding.declaredKeys.action,
      `${actionsKey}[${index}].`,
      dropped,
    );

    // Each interest record is filtered against the RECORD's declared keys.
    if (recordsKey && Array.isArray(kept[recordsKey])) {
      kept[recordsKey] = (kept[recordsKey] as unknown[]).map((row, rowIndex) => {
        if (!row || typeof row !== "object" || Array.isArray(row)) return row;
        return filterDeclared(
          row as Record<string, unknown>,
          binding.declaredKeys.interestRecord,
          `${actionsKey}[${index}].${recordsKey}[${rowIndex}].`,
          dropped,
        );
      });
    }

    return kept;
  });

  const runLevel = filterDeclared(
    { ...runLevelValues, ...(injected ?? {}) },
    binding.declaredKeys.runLevel,
    "",
    dropped,
  );

  const inputs: Record<string, unknown> = {
    ...runLevel,
    [actionsKey]: builtActions,
  };

  // The reply-draft key is written EXPLICITLY on every build, `true` or `false`.
  // The consumer's own default cannot be relied on: omitting a `false` silently
  // becomes "draft the reply", which is the exact opt-out AC-5 exists to make
  // real. `replyDraftValue` is the SAME rule `ReplyDraftControl` renders from.
  const replyDraftKey = binding.roles.replyDraft;
  if (replyDraftKey) {
    inputs[replyDraftKey] = replyDraftValue(runLevelValues[replyDraftKey]);
  }

  return { inputs, dropped };
}
